import { useCallback, useMemo, useState } from 'react';
import '../Sideoverlay/sideoverlay.scss';

import HeaderBarNew from '@root/components/HeaderBarNew';
// import workload12 from '@assets/svg/icons/workload2.svg';
import { Box, Tooltip } from '@mui/material';
// import NoData from '@root/components/common/NoData';
import agent from '@root/agent';
import NoData from '@root/components/common/NoData';
import FilterComponent from '@root/components/FilterNew';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
import CircularCloseButton from '@root/components/v2/ui-elements/CircularCloseButton';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import useSortData from '@root/hooks/useSortData';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { capitalizeFirstLetter } from '@root/utilities/string';
import isBoolean from 'lodash/isBoolean';
import { toast } from 'sonner';
import { k8columns } from './columns';
import DetailsPanel from './DetailsPanel';
import { exportPipeline } from './exportPipeline';
import SideOverlayTabMenu, { SideOverlayBody } from '@root/components/v2/SideOverlayTabMenu';
import { HOST_VULNERABILITY_MENU_CATEGORIES } from './tabMenu';

// import { CvesWorkloadColumns } from './cvesSideoverlayColumns';
// import StackedSideOverlay from './StackedSideOverlay';
const EXPORTED_HEADERNAME = [
  'Account ID',
  'Cluster',
  'CVE ID',
  'CVSS Severity',
  'CVSS Score',
  'Vulnerability Type',
  'Package Type',
  'EPSS Score',
  'Exploitable',
  'Fix Version',
];

const EXPORTED_COLS = [
  'account_id',
  'cluster',
  'cve_id',
  'severity',
  'cvss_score',
  'vul_classification',
  'package_type',
  'exploitability_score',
  'exploitable',
  'fix_version',
];

const SideOverlay = props => {
  const { selectedRow, onClose, zIndex = 300 } = props;
  const [currentSubTab, setCurrentSubTab] = useState('Vulnerabilities');
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1); // mui counts pages from 0
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedFilters, setSelectedFilters] = useState({
    cve_id: [],
    fixable: [],
    severity: [],
    package_name: [],
    vulnerability_type: [],
  });
  const [selectedCVE, setSelectedCVE] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const { sortData, handleSortModelChange } = useSortData();
  const debouncedSearchString = useDebouncedValue(searchString, 750);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const { data: vulsFilter, isFetching: isFetchingFilterKeys } = useEnhancedQuery(
    ['get_node_vulns_filter', selectedRow],
    () =>
      agent.Kubernetes.get_node_vulns_filter({
        environments: [selectedRow.account_id],
        clusters: [selectedRow.cluster],
        node_name: selectedRow.node_name,
      }),
  );

  const { data: nodesVulData, isFetching: isFetchingNodesVulnsData } = useEnhancedQuery(
    [
      'get_node_vulns_detail',
      debouncedSearchString,
      selectedRow,
      selectedFilters,
      // selectedEnvironmentTags,
      currentPage,
      pageSize,
      sortData,
    ],
    () =>
      agent.Kubernetes.get_node_vulns_detail({
        // scope: selectedEnvironmentTags,

        environment_id: selectedRow.account_id,
        cluster: selectedRow.cluster,
        node_name: selectedRow.node_name,

        search_query: debouncedSearchString ? debouncedSearchString : '',

        severities: selectedFilters.severity,
        package_names: selectedFilters.package_name,
        cve_ids: selectedFilters?.cve_id,
        vulnerability_types: selectedFilters.vulnerability_type,
        fixable:
          selectedFilters?.fixable.length === 0 ? null : selectedFilters.fixable[selectedFilters.fixable.length - 1], // true, false, null

        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        page_number: currentPage,
        rows_per_page: pageSize,
        export: false,
      }),
  );
  const handleExportData = useCallback(async () => {
    toast.info('Exporting data...', {
      position: 'top-center',
    });

    try {
      const response = await agent.Kubernetes.get_node_vulns_detail({
        // scope: selectedEnvironmentTags,

        environment_id: selectedRow.account_id,
        cluster: selectedRow.cluster,
        node_name: selectedRow.node_name,

        search_query: debouncedSearchString ? debouncedSearchString : '',

        severities: selectedFilters.severity,
        package_names: selectedFilters.package_name,
        vulnerability_types: selectedFilters.vulnerability_type,
        cve_ids: selectedFilters?.cve_id,
        fixable:
          selectedFilters?.fixable.length === 0 ? null : selectedFilters.fixable[selectedFilters.fixable.length - 1], // true, false, null

        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        page_number: currentPage,
        rows_per_page: pageSize,
        export: true,
      });

      if (response?.data) {
        const data = response.data?.map(item => {
          const _item = {};
          for (const key of EXPORTED_COLS) {
            if (isBoolean(item[key])) {
              _item[key] = JSON.stringify(item[key]);
            } else if (item[key]) {
              _item[key] = item[key];
            } else {
              _item[key] = '-';
            }
          }
          return {
            ..._item,
            exploitable: JSON.stringify(_item['exploitable']),
            vul_classification: JSON.stringify(_item['vul_classification']?.vulnerability_type),
          };
        });

        exportPipeline(data, EXPORTED_COLS, EXPORTED_HEADERNAME, 'K8 Host Vulnerabilities');
      }

      toast.success('Data exported successfully', {
        position: 'top-center',
      });
    } catch (err) {
      console.log(err);
      toast.error(`Failed to export data. ${err.message}`, {
        position: 'top-center',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortData, debouncedSearchString, currentPage, pageSize]);

  const handleCVERowClick = useCallback(r => {
    setSelectedCVE(r.row);
    setShowDetailsPanel(true);
  }, []);

  const handleCloseDetailsPanel = useCallback(() => {
    setShowDetailsPanel(false);
    setSelectedCVE(null);
  }, []);

  const propertyValues = useMemo(() => {
    if (isFetchingFilterKeys) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(vulsFilter?.data)
        // ?.filter(property => property[1].length > 0)
        ?.map(([key, values]) => [
          // Transform key
          key
            ?.split('_')
            ?.map(word => capitalizeFirstLetter(word))
            ?.join(' '),

          // Transform array values
          values.map(value => value),
        ]),
    );
  }, [isFetchingFilterKeys, vulsFilter?.data]);

  const rows = useMemo(() => {
    if (isFetchingNodesVulnsData) return [];
    return (
      nodesVulData?.data?.map((item, idx) => ({
        ...item,
        id: idx + 1,
      })) ?? []
    );
  }, [isFetchingNodesVulnsData, nodesVulData?.data]);

  return (
    <div className="k8Vulnebarlaity">
      <div className={`Resources__Backdrop2`} />
      <div className={`Resources__DataSidebar2 Resources__DataSidebar2--menu`}>
        <div className={`Resources__SidebarHeader`}>
          <div style={{ display: 'flex', paddingTop: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontFamily: 'Manrope' }}>
                {/* <img src={workload12} /> */}
                <span
                  className="text font-16 weight-600"
                  style={{
                    marginLeft: '10px',
                    color: 'var(--secondary-500)',
                    lineHeight: 1,
                  }}>
                  {selectedRow.cluster}
                </span>
                <span
                  className="text font-14 weight-500"
                  style={{
                    marginLeft: '10px',
                    color: 'var(--secondary-400)',
                    lineHeight: 1,
                  }}>
                  {selectedRow.component
                    ? selectedRow.component
                    : selectedRow.container_name
                      ? selectedRow.container_name
                      : selectedRow.architecture
                        ? selectedRow.architecture
                        : selectedRow.artifact_id}
                </span>
                {/* <Box flex={1} width={'1100px'} /> */}
              </div>
            </div>
            <Box display={'flex'} alignItems={'center'} gap={'15px'}>
              <Tooltip title="Viper AI is your AI security analyst"> </Tooltip>
              <CircularCloseButton onClick={() => onClose()} />
            </Box>
          </div>

          <div
            className="text font-16 weight-600"
            style={{
              color: 'var(--secondary-600)',
              paddingLeft: '10px',
            }}>
            {selectedRow?.service}
          </div>
        </div>

        <div className="Resources__SidebarContent CustomScrollbar">
          <SideOverlayBody
            menu={
              <SideOverlayTabMenu
                categories={HOST_VULNERABILITY_MENU_CATEGORIES}
                activeTab={currentSubTab}
                onChange={setCurrentSubTab}
                ariaLabel="Host vulnerability sections"
              />
            }>
            <div className="Resources__SidebarTabContent CustomScrollbar" style={{ paddingBottom: '24px' }}>
              <HeaderBarNew
                // marginTop={'5px'}
                marginBottom={'13px'}
                title={''}
                searchBar
                searchBarValue={searchString}
                setOpenFilter={setOpenFilter}
                openFilter={openFilter}
                exportButton={true}
                disableExportMenu
                onExportButtonClick={handleExportData}
                setSearchTerm={setSearchString}
                searchBarOnChange={e => setSearchString(e.target.value)}
              />{' '}
              <FilterComponent
                openFilter={openFilter}
                option={propertyValues}
                setSelectedFilters={setSelectedFilters}
                selectedFilters={selectedFilters}
                // isKubernetes={true}
                defaultValue={{}}
                zIndex={zIndex}
              />
              <MuiTable
                columns={k8columns()}
                paginationMode={'server'}
                LoaderState={isFetchingNodesVulnsData}
                rows={rows}
                components={{
                  NoRowsOverlay: () => <NoData message={'No Vulnerabilities present!'} />,
                }}
                sortingMode="server"
                handleSortModelChange={handleSortModelChange}
                onPageChange={setCurrentPage}
                total_size={nodesVulData?.pagination?.total}
                pageSize={pageSize}
                tableHeight={'63vh'}
                page={currentPage}
                onPageSizeChange={setPageSize}
                onRowClick={handleCVERowClick}
                noRowsMessage={'No Vulnerabilities Present!'}
                rowHeight={49.9}
                isDynamicHeight={true}
              />
              {showDetailsPanel && (
                <DetailsPanel onClose={handleCloseDetailsPanel} selectedRow={selectedCVE} type={'host_level'} />
              )}
            </div>
          </SideOverlayBody>
        </div>
      </div>
    </div>
  );
};
export default SideOverlay;
